/**
 * 全局布局组件
 * 负责：导航栏、路由渲染、字典数据加载、埋点脚本注入
 */
import React, { useMemo, useEffect, useCallback } from 'react';
import { Switch, Route, Redirect, useHistory } from 'react-router-dom';
import { Layout, Spin } from '@arco-design/web-react';
import cs from 'classnames';
import { useAppStore } from '@/store/appStore';
import NProgress from 'nprogress';
import Navbar from './components/NavBar';
import Footer from './components/Footer';
import useRoute from '@/routes';
import getUrlParams from './utils/getUrlParams';
import lazyload from './utils/lazyload';
import styles from './style/layout.module.less';
import { getDicValueApi } from '@/request/api';
import { umamiTrack } from '@/utils/index';

const Content = Layout.Content;
const NAVBAR_HEIGHT = 60;
const UMAMI_SCRIPT_URL = 'http://101.200.84.209:3000/script.js';
const DEFAULT_WEB_ID = '9e1e5f7a-607b-499f-a6b4-13c71b2c35f4';

// 埋点映射表
const TRACK_MAP: Record<string, string> = {
  'menu.system.pro': '专题项目',
  'menu.knowledge.graph': '知识图谱',
  'menu.knowledgeCheck.list': '知识查查',
  'menu.system.scheme': '培养方案',
};

// 加载埋点脚本
function loadScript(src: string, dataWebsiteId: string) {
  const script = document.createElement('script');
  script.defer = true;
  script.src = src;
  script.dataset.websiteId = dataWebsiteId;
  document.body.appendChild(script);
}

// 扁平化路由并添加懒加载
function getFlattenRoutes(routes) {
  const res = [];
  function travel(_routes) {
    _routes.forEach((route) => {
      const visibleChildren = (route.children || []).filter((child) => !child.ignore);
      if (route.key && (!route.children || !visibleChildren.length)) {
        try {
          route.component = lazyload(() => import(`./pages/${route.key}`));
          res.push(route);
        } catch (e) {
          console.error(e);
        }
      }
      if (route.children && route.children.length) {
        travel(route.children);
      }
    });
  }
  travel(routes);
  return res;
}

function PageLayout() {
  const history = useHistory();
  const { settings, userLoading, userInfo } = useAppStore((state) => ({
    settings: state.global.settings,
    userLoading: state.global.userLoading,
    userInfo: state.global.userInfo,
  }));
  const setDictionary = useAppStore((state) => state.setDictionary);

  // 所有登录用户都能看到所有路由，不进行权限和角色过滤
  const [routes, defaultRoute] = useRoute(userInfo?.permissions);
  const flattenRoutes = useMemo(() => getFlattenRoutes(routes), [routes]);

  // 控制导航栏和页脚显示
  const urlParams = getUrlParams();
  const showNavbar = settings.navbar && urlParams.navbar !== false;
  const showFooter = settings.footer && urlParams.footer !== false;

  // 加载字典数据
  const getDictionary = useCallback(async () => {
    try {
      const codes = [
        'special_subject_type',
        'stu_grade',
        'stu_class',
        'semester',
        'course_system_type',
        'course_type',
      ];
      const results = await Promise.all(
        codes.map((code) => getDicValueApi({ code }))
      );

      setDictionary({
        specialSubjectType: results[0].data,
        stuGrade: results[1].data,
        stuClass: results[2].data,
        semester: results[3].data,
        courseSystemType: results[4].data,
        courseType: results[5].data,
      });
    } catch (error) {
      console.error('获取字典数据失败:', error);
    }
  }, [setDictionary]);

  // 初始化：加载字典数据和埋点脚本
  useEffect(() => {
    getDictionary();
    const webId = process.env.REACT_APP_WEBID || DEFAULT_WEB_ID;
    loadScript(UMAMI_SCRIPT_URL, webId);
  }, [getDictionary]);


  // 菜单项点击处理：预加载组件并跳转
  const onClickMenuItem = useCallback((key: string) => {
    const currentRoute = flattenRoutes.find((r) => r.key === key);
    if (!currentRoute) return;

    // 埋点上报
    const label = TRACK_MAP[currentRoute.name];
    if (label) {
      umamiTrack(window, '导航标签', { nodeType: '导航标签', nodeName: label });
    }

    // 预加载并跳转
    NProgress.start();
    currentRoute.component
      .preload()
      .then(() => {
        history.push(currentRoute.path || `/${key}`);
        NProgress.done();
      })
      .catch(() => {
        NProgress.done();
      });
  }, [flattenRoutes, history]);

  const paddingStyle = showNavbar ? { paddingTop: NAVBAR_HEIGHT } : {};
  const isGraphPage = history.location.pathname === '/knowledge/graph';

  return (
    <Layout className={styles.layout}>
      <div className={cs(styles['layout-navbar'], { [styles['layout-navbar-hidden']]: !showNavbar })}>
        <Navbar
          show={showNavbar}
          routes={routes}
          onClickMenuItem={onClickMenuItem}
        />
      </div>
      {(userLoading || !userInfo) ? (
        <Spin className={styles['spin']} />
      ) : (
        <Layout>
          <Layout className={styles['layout-content']} style={paddingStyle}>
            <div className={cs(styles['layout-content-wrapper'], 'main-content', {
              [styles['layout-content-graph']]: isGraphPage,
            })}>
              <Content>
                <Switch>
                  {flattenRoutes.map((route) => (
                    <Route key={route.key} path={`/${route.key}`} component={route.component} />
                  ))}
                  <Route exact path="/">
                    <Redirect to={`/${defaultRoute}`} />
                  </Route>
                  <Route path="*" component={lazyload(() => import('./pages/exception/403'))} />
                </Switch>
              </Content>
            </div>
            {showFooter && <Footer />}
          </Layout>
        </Layout>
      )}
    </Layout>
  );
}

export default PageLayout;
